/**
 * DomainEnrollPage
 *
 * Shows pricing details and a payment flow for paid domains.
 * For child domains (Pattern B subdomains), shows a prerequisite
 * progress bar before allowing enrollment.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { marketplaceAPI, characterAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CharacterPickerModal from '../components/CharacterPickerModal';

function PrerequisiteBar({ skill, current, required }) {
  const pct = Math.min(100, Math.round((current / required) * 100));
  const met = current >= required;
  return (
    <div className="dep-prereq-bar">
      <div className="dep-prereq-info">
        <span className="dep-prereq-skill">{skill}</span>
        <span className={`dep-prereq-val ${met ? 'met' : 'unmet'}`}>
          {current} / {required}
        </span>
      </div>
      <div className="dep-bar-track">
        <div
          className={`dep-bar-fill ${met ? 'met' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function DomainEnrollPage() {
  const { domainId } = useParams();
  const { learner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const listing = location.state?.listing || null;
  const pricingInfo = location.state?.pricing || null;

  const [prerequisites, setPrerequisites] = useState(null);
  const [prereqPassed, setPrereqPassed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [error, setError] = useState(null);
  // Character archetype picker state
  const [archetypeModal, setArchetypeModal] = useState(null); // { characterClasses, characterDisplayName }

  useEffect(() => {
    if (!learner) return;
    marketplaceAPI.checkPrerequisites(domainId, { learner_id: learner.learner_id })
      .then(({ data }) => {
        setPrerequisites(data.required_skills || {});
        setPrereqPassed(data.passed);
      })
      .catch(() => {
        setPrereqPassed(true); // if check fails, assume passed
      })
      .finally(() => setLoading(false));
  }, [learner, domainId]);

  const goToCharacterSelect = () => navigate(`/character-select/${domainId}`);

  const handleArchetypeSelect = async (classId) => {
    try {
      await characterAPI.selectCharacter(domainId, classId);
    } catch { /* non-fatal */ }
    setArchetypeModal(null);
    goToCharacterSelect();
  };

  const handleArchetypeSkip = async (defaultClassId) => {
    if (defaultClassId) {
      try {
        await characterAPI.selectCharacter(domainId, defaultClassId);
      } catch { /* non-fatal */ }
    }
    setArchetypeModal(null);
    goToCharacterSelect();
  };

  const handleEnroll = async () => {
    if (!prereqPassed) { setError('You must meet the prerequisite skills before enrolling.'); return; }
    setEnrolling(true);
    setError(null);
    try {
      await marketplaceAPI.enroll({
        learner_id: learner.learner_id,
        domain_id: domainId,
        stripe_payment_method_id: paymentMethod || undefined,
      });

      // Check if domain has character archetypes enabled
      try {
        const { data: archetypeData } = await characterAPI.getCharacterClasses(domainId);
        if (archetypeData?.character_archetypes_enabled && archetypeData?.character_classes?.length > 0) {
          setArchetypeModal({
            characterClasses: archetypeData.character_classes,
            characterDisplayName: archetypeData.character_display_name || 'Learning Path',
          });
          return; // modal will navigate after selection
        }
      } catch { /* non-fatal — proceed to character select */ }

      goToCharacterSelect();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) return <div className="dep-loading">Checking prerequisites...</div>;

  const hasPrereqs = prerequisites && Object.keys(prerequisites).length > 0;
  const isPaid = !!pricingInfo;

  return (
    <div className="dep-page">
      {/* Character archetype picker — shown after enrollment when archetypes are enabled */}
      {archetypeModal && (
        <CharacterPickerModal
          domain={domainId}
          characterClasses={archetypeModal.characterClasses}
          characterDisplayName={archetypeModal.characterDisplayName}
          onSelect={handleArchetypeSelect}
          onSkip={handleArchetypeSkip}
        />
      )}

      <button className="dep-back-btn" onClick={() => navigate(-1)}>← Back</button>

      <div className="dep-card">
        <h2>{listing?.title || domainId}</h2>
        <p className="dep-desc">{listing?.short_description}</p>

        {/* Prerequisites */}
        {hasPrereqs && (
          <div className="dep-prereq-section">
            <h4>Prerequisites</h4>
            <p className="dep-prereq-note">
              You need the following skills from the parent domain to enroll:
            </p>
            {Object.entries(prerequisites).map(([skill, info]) => (
              <PrerequisiteBar
                key={skill}
                skill={skill}
                current={info.current || 0}
                required={info.required || 0}
              />
            ))}
            {!prereqPassed && (
              <div className="dep-prereq-warning">
                Complete the parent domain requirements first to unlock this domain.
              </div>
            )}
          </div>
        )}

        {/* Pricing */}
        {isPaid && (
          <div className="dep-pricing-section">
            <h4>Pricing</h4>
            <div className="dep-price-tag">
              ${(pricingInfo.price_cents / 100).toFixed(2)} {pricingInfo.currency?.toUpperCase()}
              {pricingInfo.pricing_model === 'subscription' && '/month'}
            </div>
            <div className="dep-payment-input">
              <label>Stripe Payment Method ID (test)</label>
              <input
                type="text"
                placeholder="pm_card_visa"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              />
              <p className="dep-payment-note">
                In production this is handled by Stripe Elements embedded in the page.
              </p>
            </div>
          </div>
        )}

        {error && <div className="dep-error">{error}</div>}

        <button
          className="dep-enroll-btn"
          onClick={handleEnroll}
          disabled={enrolling || !prereqPassed}
        >
          {enrolling ? 'Processing...' : isPaid ? 'Pay & Enroll' : 'Enroll Free'}
        </button>
      </div>

      <style>{`
        .dep-page { min-height: 100vh; background: #0f172a; color: #e2e8f0; padding: 2rem; font-family: system-ui, sans-serif; display: flex; flex-direction: column; align-items: center; }
        .dep-loading { display: flex; justify-content: center; align-items: center; min-height: 100vh; color: #94a3b8; }
        .dep-back-btn { align-self: flex-start; background: none; border: 1px solid rgba(255,255,255,0.2); color: #94a3b8; padding: 0.4rem 0.8rem; border-radius: 0.5rem; cursor: pointer; margin-bottom: 2rem; }
        .dep-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 1.25rem; padding: 2rem; max-width: 560px; width: 100%; display: flex; flex-direction: column; gap: 1.5rem; }
        .dep-card h2 { margin: 0; font-size: 1.5rem; }
        .dep-desc { color: #94a3b8; margin: 0; font-size: 0.9rem; }
        .dep-prereq-section h4, .dep-pricing-section h4 { margin: 0 0 0.75rem; font-size: 1rem; color: #a78bfa; }
        .dep-prereq-note { color: #94a3b8; font-size: 0.85rem; margin: 0 0 1rem; }
        .dep-prereq-bar { display: flex; flex-direction: column; gap: 0.25rem; margin-bottom: 0.75rem; }
        .dep-prereq-info { display: flex; justify-content: space-between; font-size: 0.85rem; }
        .dep-prereq-skill { color: #e2e8f0; }
        .dep-prereq-val.met { color: #34d399; }
        .dep-prereq-val.unmet { color: #f87171; }
        .dep-bar-track { height: 6px; background: rgba(255,255,255,0.1); border-radius: 9999px; overflow: hidden; }
        .dep-bar-fill { height: 100%; background: #f87171; border-radius: 9999px; transition: width 0.3s; }
        .dep-bar-fill.met { background: #34d399; }
        .dep-prereq-warning { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); padding: 0.6rem 1rem; border-radius: 0.5rem; color: #fca5a5; font-size: 0.85rem; }
        .dep-price-tag { font-size: 1.75rem; font-weight: 700; color: #fbbf24; }
        .dep-payment-input { display: flex; flex-direction: column; gap: 0.4rem; }
        .dep-payment-input label { font-size: 0.8rem; color: #94a3b8; }
        .dep-payment-input input { padding: 0.5rem 0.75rem; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.15); border-radius: 0.5rem; color: #e2e8f0; font-size: 0.9rem; }
        .dep-payment-note { font-size: 0.75rem; color: #64748b; margin: 0; }
        .dep-error { background: rgba(239,68,68,0.15); border: 1px solid rgba(239,68,68,0.4); padding: 0.6rem 0.9rem; border-radius: 0.5rem; color: #fca5a5; font-size: 0.85rem; }
        .dep-enroll-btn { padding: 0.75rem 2rem; background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; border: none; border-radius: 0.75rem; cursor: pointer; font-size: 1rem; font-weight: 700; }
        .dep-enroll-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
